import { css } from "@emotion/react";
import { type ComponentType, Fragment } from "react";

import EmptyView from "../components/EmptyView";
import { getSpaceVariable } from "../components/space/utilities";
import Text from "../components/Text";
import { LIST_ITEM_MARKER, MARGINS_BY_BLOCK_TYPE, TITLE_LEVEL_VARIANT } from "./constants";
import { getListItemMarker } from "./listItem";
import { sanitizeHtml } from "./sanitizeHtml";
import type { BriefBlock } from "./schema";
import type { BodyBlock } from "./schema/blocks/body";
import type { ListItemBlock } from "./schema/blocks/list-item";
import type { TitleBlock } from "./schema/blocks/title";

type BriefViewProps = {
    blocks: BriefBlock[];
};

type BlockViewMap = {
    title: ComponentType<{ block: TitleBlock }>;
    body: ComponentType<{ block: BodyBlock }>;
    listItem: ComponentType<{ block: ListItemBlock; marker: string }>;
};

const renders: BlockViewMap = {
    title: TitleBlockView,
    body: BodyBlockView,
    listItem: ListItemBlockView,
};

export default function BriefView(props: BriefViewProps) {
    const { blocks } = props;
    return (
        <div>
            {blocks.length === 0 ? (
                <EmptyView message="Aucun contenu pour le moment" />
            ) : (
                blocks.map((block, index) => (
                    <Fragment key={block.id}>{renderBlock(block, index, blocks)}</Fragment>
                ))
            )}
        </div>
    );
}

function renderBlock(block: BriefBlock, index: number, blocks: BriefBlock[]) {
    switch (block.type) {
        case "title": {
            const Render = renders.title;
            return <Render block={block} />;
        }
        case "body": {
            const Render = renders.body;
            return <Render block={block} />;
        }
        case "listItem": {
            const Render = renders.listItem;
            return <Render block={block} marker={getListItemMarker(blocks, index)} />;
        }
    }
}

function TitleBlockView(props: { block: TitleBlock }) {
    const { block } = props;
    return (
        <div id={block.anchor ?? undefined} css={MARGINS_BY_BLOCK_TYPE[block.type]}>
            <Text variant={TITLE_LEVEL_VARIANT[block.level]}>{block.text}</Text>
        </div>
    );
}

function BodyBlockView(props: { block: BodyBlock }) {
    const { block } = props;
    return (
        <Text variant="body" css={MARGINS_BY_BLOCK_TYPE[block.type]}>
            <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.html) }} />
        </Text>
    );
}

function ListItemBlockView(props: { block: ListItemBlock; marker: string }) {
    const { block, marker } = props;
    return (
        <Text
            variant="body"
            css={css(MARGINS_BY_BLOCK_TYPE[block.type], {
                paddingInlineStart: `calc(${block.depth} * ${getSpaceVariable(6)})`,
            })}
        >
            <span aria-hidden css={LIST_ITEM_MARKER}>
                {marker}
            </span>
            <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.html) }} />
        </Text>
    );
}
